import { CompliancePageDealDocumentsEmailTrackingListQuery } from '@/__generated__/graphql';
import { InfoIcon } from '@/assets/icons/info-icons/info';
import { EllipsisWithTooltip } from '@/components/basicComponents/ellipsisWithTooltip';
import { Tooltip } from '@/components/basicComponents/tooltip';
import { Loader } from '@/components/loaders/loader';
import NoResults from '@/components/table/noResults';
import RedirectTitleCell from '@/components/table/redirectTitleCell';
import {
  TableBodyWrapper,
  TableFixedCell,
  TableRowCell,
  TableScrollableCellsWrapper,
  TableWithHorizontalScroll,
  TableWithHorizontalScrollRowWrapper,
  TableWithHorizontalScrollWrapper,
  TextCell
} from '@/components/table/styles';
import TableHeader from '@/components/table/tableHeader';
import { ISort, TableColumns } from '@/components/table/types';
import { useAuth } from '@/hooks/use-auth';
import { useResponsive } from '@/hooks/use-responsive';
import { getUTCDate } from '@/shared/utils/date';
import guid from '@/shared/utils/guid';
import { themeVar } from '@/styles/theme';
import { TenantType } from '@trace-8/trace8-schema';
import _ from 'lodash';
import styled from 'styled-components';
import { dealDocumentsTrackingTableEmailTrackingCellTooltips } from '../../constants';

export const getDealDocumentsTrackingTableDocsSentCellValue = (assetFileNames: string[]): React.ReactNode => {
  if (!assetFileNames?.length) return <>N/A</>;

  if (assetFileNames?.length === 1) {
    return (
      <EllipsisWithTooltip title={assetFileNames[0]}>
        <TextCell>{assetFileNames[0]}</TextCell>
      </EllipsisWithTooltip>
    );
  }

  return (
    <Tooltip title={assetFileNames.join(', ')}>
      <TextCell>Multiple ({assetFileNames?.length})</TextCell>
    </Tooltip>
  );
};

interface DealDocumentsTrackingTableProps {
  tableColumns: TableColumns[];
  tableRows: CompliancePageDealDocumentsEmailTrackingListQuery['dealDocumentsEmailTrackingList']['dealDocumentTrackingData'];
  sort: ISort;
  loading: boolean;
  setSort: (value: ISort) => void;
  openPage: (path: string) => void;
}

export const DealDocumentsTrackingTable = ({ tableColumns, tableRows, sort, loading, setSort, openPage }: DealDocumentsTrackingTableProps) => {
  const { isTablet } = useResponsive();
  const { user } = useAuth();
  const isPlatformManagerUser = user && user?.tenant?.type === TenantType.PLATFORM_MANAGER;

  // 5 is the gap (spacing) between table cells
  const totalTableWidth = tableColumns.reduce((acc, column) => acc + column.width + 5, 0);

  return (
    <TableWithHorizontalScrollWrapper isTablet={isTablet}>
      <TableWithHorizontalScroll totalWidth={`${totalTableWidth}px`}>
        <TableHeader refetch={() => {}} columns={tableColumns} savedSort={sort} savedSetSort={setSort} withHorizontalScroll />
        <TableBodyWrapper>
          {loading ? (
            <Loader />
          ) : (
            <>
              {!tableRows.length ? (
                <NoResults />
              ) : (
                <>
                  {tableRows.map((row) => (
                    <TableWithHorizontalScrollRowWrapper key={guid()}>
                      <TableFixedCell id="fixedCell" width={tableColumns[0].width} alignItems="center">
                        <RedirectTitleCell
                          onClick={() =>
                            openPage(
                              `/investors/${row?.allocation?.legalEntity?.family?.id}/${row?.allocation?.legalEntity?.id}?backToTitle=Send Deal Documents Tracking`
                            )
                          }
                        >
                          {row?.allocation?.legalEntity?.entityName}
                        </RedirectTitleCell>
                      </TableFixedCell>
                      <TableScrollableCellsWrapper alignItems="center">
                        <TableRowCell width={tableColumns[1].width} withHorizontalScroll>
                          <RedirectTitleCell
                            onClick={() => openPage(`/strategyDetails/${row?.allocation?.investment?.id}?backToTitle=Send Deal Documents Tracking`)}
                          >
                            {row?.allocation?.investment?.name}
                          </RedirectTitleCell>
                        </TableRowCell>
                        <TableRowCell width={tableColumns[2].width} withHorizontalScroll>
                          <TextCell>{row?.action}</TextCell>
                        </TableRowCell>
                        <TableRowCell width={tableColumns[3].width} withHorizontalScroll>
                          <TextCell>{!!row?.actionDate && getUTCDate(row?.actionDate).toUpperCase()}</TextCell>
                        </TableRowCell>
                        <TableRowCell width={tableColumns[4].width} withHorizontalScroll>
                          <TextCell>{row?.actionPerformedBy}</TextCell>
                        </TableRowCell>
                        <TableRowCell width={tableColumns[5].width} withHorizontalScroll>
                          <TextCell>{row?.emailRecipient}</TextCell>
                        </TableRowCell>
                        <TableRowCell width={tableColumns[6].width} withHorizontalScroll>
                          {getDealDocumentsTrackingTableDocsSentCellValue(row?.assetFileNames)}
                        </TableRowCell>
                        <TableRowCell width={tableColumns[7].width} withHorizontalScroll>
                          <TextCell>
                            {!!row?.emailTrackingStatus && (
                              <EmailTrackingStatusCellWrapper>
                                {_.startCase(row?.emailTrackingStatus.toLowerCase())}
                                <Tooltip
                                  title={dealDocumentsTrackingTableEmailTrackingCellTooltips[row?.emailTrackingStatus]}
                                  disableTooltip={!dealDocumentsTrackingTableEmailTrackingCellTooltips[row?.emailTrackingStatus]}
                                >
                                  <InfoIcon fill={themeVar('font.weak')} style={{ position: 'relative', top: '3px' }} />
                                </Tooltip>
                              </EmailTrackingStatusCellWrapper>
                            )}
                          </TextCell>
                        </TableRowCell>
                        <TableRowCell width={tableColumns[8].width} withHorizontalScroll>
                          <TextCell>{row?.allocation?.operationsWorkflowTransitionTask?.name}</TextCell>
                        </TableRowCell>
                        <TableRowCell width={tableColumns[9].width} withHorizontalScroll>
                          <TextCell>{!!row?.allocation?.lastUpdatedAt && getUTCDate(row?.allocation?.lastUpdatedAt).toUpperCase()}</TextCell>
                        </TableRowCell>
                        <TableRowCell width={tableColumns[10].width} withHorizontalScroll>
                          {isPlatformManagerUser && <TextCell>{row?.allocation?.tenant?.name}</TextCell>}
                        </TableRowCell>
                      </TableScrollableCellsWrapper>
                    </TableWithHorizontalScrollRowWrapper>
                  ))}
                </>
              )}
            </>
          )}
        </TableBodyWrapper>
      </TableWithHorizontalScroll>
    </TableWithHorizontalScrollWrapper>
  );
};

const EmailTrackingStatusCellWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;
